import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ProfinderLog } from '@engine/models/admin'

import { AdminListLayout } from '#components/AdminListLayout'
import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import { ScacTypeahead } from '#components/ScacTypeahead.js'
import { Slider } from '#components/Slider'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'
import {
  getRangeOptions,
  RANGE_MINUTES,
  type RangeKey,
} from '#utils/timeRanges'

import { CopyProfinderRequestButton } from './CopyProfinderRequestButton'
import { ProfinderLogDetails } from './ProfinderLogDetails'
import { ProfinderLogsTable } from './ProfinderLogsTable'

type ProfinderFilters = {
  carrier: string
  externalId: string
  result: 'all' | 'picked up' | 'found' | 'notfound'
  range: RangeKey
  customFrom: string
  customTo: string
}

const ProfinderLogsView = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, setFilters] = useState<ProfinderFilters>({
    carrier: searchParams.get('carrier') || '',
    externalId: searchParams.get('externalId') || '',
    result: (searchParams.get('result') as ProfinderFilters['result']) || 'all',
    range: (searchParams.get('range') as RangeKey) || '30d',
    customFrom: searchParams.get('customFrom') || '',
    customTo: searchParams.get('customTo') || '',
  })
  const [selectedLog, setSelectedLog] = useState<ProfinderLog | undefined>(
    undefined,
  )

  const queryParams = useMemo(() => {
    const base: Record<string, string | number> = {
      limit: 50,
      sort: '-timestamp',
      logSource: 'profinder',
    }

    if (filters.carrier.trim()) {
      base.carrier = filters.carrier.trim()
    }

    if (filters.externalId.trim()) {
      base.externalId = filters.externalId.trim()
    }

    if (filters.result !== 'all') {
      base.result = filters.result
    }

    const now = new Date()

    if (filters.range === 'custom') {
      if (filters.customFrom) {
        base.from = new Date(filters.customFrom).toISOString()
      }
      if (filters.customTo) {
        base.to = new Date(filters.customTo).toISOString()
      }
    } else if (filters.range !== 'all') {
      const minutes = RANGE_MINUTES[filters.range]
      const from = new Date(now.getTime() - minutes * 60 * 1_000)
      base.from = from.toISOString()
      base.to = now.toISOString()
    }
    // When range is 'all', don't set from/to constraints

    return base
  }, [filters])

  const autoRefreshMs = undefined

  const {
    items,
    error,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<ProfinderLog>('profinder-logs', queryParams, {
    autoRefreshMs,
    enabled: true,
  })

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (filters.carrier) params.set('carrier', filters.carrier)
    if (filters.externalId) params.set('externalId', filters.externalId)
    if (filters.result !== 'all') params.set('result', filters.result)
    if (filters.range !== '30d') params.set('range', filters.range)
    if (filters.customFrom) params.set('customFrom', filters.customFrom)
    if (filters.customTo) params.set('customTo', filters.customTo)
    setSearchParams(params, { replace: true })
  }, [
    filters.carrier,
    filters.externalId,
    filters.result,
    filters.range,
    filters.customFrom,
    filters.customTo,
    setSearchParams,
  ])

  const handleFilterChange = <Key extends keyof ProfinderFilters>(
    key: Key,
    value: ProfinderFilters[Key],
  ) => {
    setFilters(previous => ({
      ...previous,
      [key]: value,
    }))
    setSelectedLog(undefined)
  }

  const sliderTitle = (
    <div className="flex items-center gap-4">
      <span>Log Details</span>
      {selectedLog && <CopyProfinderRequestButton log={selectedLog} />}
    </div>
  )

  return (
    <>
      <AdminListLayout
        title="ProFinder - GCP Logs"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No ProFinder GCP logs found"
        filters={
          <AdminFilters isLoading={isLoading} onRefresh={refresh}>
            <FilterRow>
              <ScacTypeahead
                value={filters.carrier}
                onChange={value => handleFilterChange('carrier', value)}
              />
              <FilterInput
                label="Load# / Load ID"
                value={filters.externalId}
                onChange={value => handleFilterChange('externalId', value)}
                placeholder="CHR load number or FV load ID"
              />
            </FilterRow>

            <FilterRow>
              <FilterSelect
                label="Result"
                value={filters.result}
                onChange={value =>
                  handleFilterChange(
                    'result',
                    value as ProfinderFilters['result'],
                  )
                }
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'picked up', label: 'Picked up' },
                  { value: 'found', label: 'Found' },
                  { value: 'notfound', label: 'Not Found' },
                ]}
              />
              <FilterSelect
                label="Time range"
                value={filters.range}
                onChange={value =>
                  handleFilterChange(
                    'range',
                    value as ProfinderFilters['range'],
                  )
                }
                options={getRangeOptions().filter(o => o.value !== 'all')}
              />
              {filters.range === 'custom' ? (
                <>
                  <FilterInput
                    label="From"
                    value={filters.customFrom}
                    onChange={value => handleFilterChange('customFrom', value)}
                    type="datetime-local"
                  />
                  <FilterInput
                    label="To"
                    value={filters.customTo}
                    onChange={value => handleFilterChange('customTo', value)}
                    type="datetime-local"
                  />
                </>
              ) : null}
            </FilterRow>
          </AdminFilters>
        }
        listContent={
          items.length > 0 ? (
            <ProfinderLogsTable
              logs={items}
              selectedLogId={selectedLog?.id}
              onSelect={log => setSelectedLog(log)}
              infiniteScrollRef={sentinelRef}
              isFetchingMore={isFetchingNextPage}
            />
          ) : null
        }
      />

      <Slider
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(undefined)}
        title={sliderTitle}
      >
        <ProfinderLogDetails log={selectedLog} />
      </Slider>
    </>
  )
}

export { ProfinderLogsView }
